/**
 * `useSidePanelEntryGuarantee` — the navigator column is VISIBLE when the lawyer
 * enters a conversation surface.
 *
 * ## The defect this closes (miniplan H1 / defect #1)
 *
 * `sidePanelCollapsed` is a single session-long flag written by three
 * AUTOMATIC paths:
 *   1. any focus pane opening (`openFocusPane` force-collapses it),
 *   2. a compact viewport, on every route change while compact,
 *   3. Esc inside the compact drawer.
 *
 * …and nothing ever guaranteed it back. The rule that blocked recovery —
 * *"we don't proactively re-open on resize-back-to-desktop; the user's last
 * manual state stands"* — was written for MANUAL collapses but silently
 * captured the automatic ones too. On desktop `/chat` and `/home` there is no
 * VISIBLE way back (only `⌘/Ctrl+B`, documented inside a help dialog), so one
 * agent-pane session left the lawyer navigator-less on every conversation
 * surface, permanently, with the case list — the way you open a case — gone.
 *
 * ## Why an entry guarantee rather than patching each collapse path
 *
 * Patching the three writers is whack-a-mole: the fourth one added later
 * re-opens the hole, silently. Asserting the INVARIANT at the moment it matters
 * ("entering a conversation route ⇒ the navigator is there") makes the exact
 * trigger irrelevant. It also stays correct if a future surface collapses the
 * panel for its own good reasons.
 *
 * ## What it deliberately does NOT override
 *
 * - **An explicit user collapse** (`sidePanelUserCollapsedAt` non-null): the
 *   lawyer asked for the width. Their intent outranks this automation, exactly
 *   as `lastUserToggleAt` gates the right sidebar's auto-expand.
 * - **A focus pane being open**: the pane needs that width; re-opening the
 *   panel underneath it would fight `openFocusPane` every render.
 * - **Compact viewports**: there the panel is a modal drawer, and forcing it
 *   open would cover the content the lawyer just navigated to.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useUIStore } from "@/stores/useUIStore";
import { isChatLeftRoute } from "@/lib/chat-left-routes";
import { isCompactViewport, useViewportSize } from "@/hooks/useViewportSize";

export function useSidePanelEntryGuarantee(): void {
  const location = useLocation();
  const viewport = useViewportSize();
  const isCompact = isCompactViewport(viewport);

  const focusPane = useUIStore((s) => s.focusPane);
  const sidePanelCollapsed = useUIStore((s) => s.sidePanelCollapsed);
  const sidePanelUserCollapsedAt = useUIStore((s) => s.sidePanelUserCollapsedAt);
  const setSidePanelCollapsed = useUIStore((s) => s.setSidePanelCollapsed);

  useEffect(() => {
    // A conversation surface is exactly what `isChatLeftRoute` already
    // classifies — `/chat` and the case workspaces. Reusing it keeps ONE
    // definition of "a route that shows the conversation" in the codebase.
    if (!isChatLeftRoute(location.pathname)) return;
    if (isCompact) return;
    if (focusPane !== null) return;
    if (sidePanelUserCollapsedAt !== null) return;
    if (!sidePanelCollapsed) return;

    // AUTOMATIC setter on purpose: restoring the panel is not the lawyer
    // expressing a preference, so it must not stamp intent (which would then
    // gate the guarantee itself on the next entry).
    setSidePanelCollapsed(false);
  }, [
    location.pathname,
    isCompact,
    focusPane,
    sidePanelCollapsed,
    sidePanelUserCollapsedAt,
    setSidePanelCollapsed,
  ]);
}
